/**
 * The wushu polish carried over to client0's rig (lane C0-POSE-FIX). On 2026-10-07 the old 3D client's motions were
 * checked against real wushu and wire-fu staging and fixed (docs/handoff/motions-polish.md, the research in
 * docs/research/POSING_RIGGING.md §5 and MOTION_SMOOTH.md); none of it reached the 2D rig. This file lays those fixes
 * over the motion groups' own key poses, so the groups stay as their lanes wrote them and the port is one switch
 * (per pose once approved, `wushuFor`, read by `rigFor` in index.ts and by the skeleton for hands and stance width).
 *
 * What is laid over, with the finding it answers (audit table in docs/research/C0_POSE_FIX.md):
 * - the free hand never hangs in a fight (剑指): forward at the guard, up in an arc over the head as the blade comes
 *   down, laid on the sword wrist in the thrust (剑指附腕);
 * - the horse stance (马步) wide, deep and upright; the palm push from an upright trunk, chambered at the belt;
 * - the trunk turning into the sheathe (收剑);
 * - qinggong laid into the flight (wire-fu): the glide pitched forward with the legs trailing, the wall run with the
 *   arms swept back, the water skim laid forward with the back leg high;
 * - folk: the salute (抱拳) on straight legs, the call beckoning with an open hand, the kowtow down on one knee.
 * Every override is a whole key pose in skeleton terms, so it holds through the coming proportion change.
 */
import { keyed, SHEATHE_FRAMES, type Rig } from './kit';
import { approved } from './review';
import type { Pose } from './index';

/**
 * Whether the polish is laid over pose `pose`: per pose, once the user has approved it in the studio's review
 * (motions/review.ts, key `pose:<id>`); the review tools force it on or off for before/after.
 */
let forced: boolean | null = null;
export const wushuFor = (pose: string): boolean => forced ?? approved(`pose:${pose}`);
/** Review tools only (tools/poseFix, tools/poses): force the polish on or off for every pose (null: per approval). */
export function setWushuForReview(v: boolean | null): void {
  forced = v;
}
/** The forced state, for a review tool to put back. */
export const wushuForced = (): boolean | null => forced;
/** The walk's forearm bend (skeleton `fore`), approved as the walk's own (`pose:walk`). */
export const wushu = (): boolean => wushuFor('walk');

type Over = (ph: number) => Partial<Rig>;

/** Sheathing: the trunk turns into the stow, most at the hip, upright again as the hand lets go. */
const stowTurn: Over = (ph) => {
  const u = (((ph % SHEATHE_FRAMES) + SHEATHE_FRAMES) % SHEATHE_FRAMES) / (SHEATHE_FRAMES - 1);
  return { tilt: Math.round(8 * Math.sin(Math.PI * u)), lean: Math.round(Math.sin(Math.PI * u)) };
};

/** Laid over each pose's own rig (`rigFor`): only the fields named change. */
export const OVERRIDES: Partial<Record<Pose, Over>> = {
  // ── the free hand in a fight (motions-polish "Combat motions": block, guardHit, hurt; all blows) ──
  block: () => ({ far: 'fwd' }),
  blockHit: () => ({ far: 'fwd' }),
  atk: () => ({ far: 'up' }),
  cut: () => ({ far: 'up' }),
  cleaveDrive: () => ({ far: 'up' }),
  cleaveThrough: () => ({ far: 'up' }),
  follow: () => ({ far: 'up' }),
  risingSweep: () => ({ far: 'up' }),
  risingHigh: () => ({ far: 'up' }),
  thrust: () => ({ far: 'fwd' }),
  thrustRecoil: () => ({ far: 'fwd' }),
  // ── stances (motions-polish #17: 马步, 弓步 trunk upright) ──
  cast: () => ({ o: 7, lean: 0, tilt: 0, f1: -1, f2: 1 }),
  gatherDown: () => ({ o: 7, lean: 0, tilt: 0, f1: -1, f2: 1 }),
  palmPush: () => ({ lean: 2, tilt: 7 }),
  // Chambered at the belt, elbows back (#13: not set on the hips).
  palmDraw: () => ({ arm: 'forward', far: 'fwd' }),
  // ── sheathe (#11) ──
  sheathe: stowTurn,
  unsheathe: stowTurn,
  // ── qinggong (#5, #8, #9) ──
  glide: () => ({ tilt: 24, lean: 2, f1: -8, f2: -4, l1: 5, l2: 7 }),
  climbA: () => ({ arm: 'back', far: 'back' }),
  climbB: () => ({ arm: 'back', far: 'back' }),
  skimA: () => ({ tilt: 14, l1: 6 }),
  skimB: () => ({ tilt: 14, l2: 6 }),
  skimMid: () => ({ tilt: 14 }),
  // ── folk (motions-polish "Life and work": bow, call, kowtow) ──
  bow: () => ({ o: 0, lean: 1, tilt: 14 }),
  call: () => ({ far: 'fwd' }),
  kowtow: (ph) => ({
    headDx: 1,
    arm: 'clasp',
    far: 'clasp',
    ...keyed(
      [
        // Upright, fist in palm; down on one knee, the salute raised (揖); bowed over the knee, held.
        { o: 1, lean: 0, tilt: 8, f1: -1, f2: 1, closed: false },
        { o: 16, lean: 1, tilt: 6, f1: -8, f2: 7, closed: false },
        { o: 17, lean: 1, tilt: 30, f1: -8, f2: 7, closed: true },
        { o: 17, lean: 1, tilt: 30, f1: -8, f2: 7, closed: true },
      ],
      ph,
    ),
  }),
};

/**
 * Hand targets for one pose (classic design coordinates, as `POSE_HANDS` in rig/skeleton.ts): the sword finger on the
 * sword wrist in the thrust, both hands chambered at the belt in the palm draw, the arms swept back on the wall run.
 */
export const HANDS: Partial<Record<Pose, { A?: [number, number]; B?: [number, number] }>> = {
  thrust: { B: [11, 28] },
  thrustRecoil: { B: [9, 29] },
  palmDraw: { A: [-2, 34], B: [-3, 34] },
  climbA: { A: [-12, 31], B: [-15, 32] },
  climbB: { A: [-12, 31], B: [-15, 32] },
};

/** How far apart the feet stand across the body (1 = the pose's default): the horse stance's wide base. */
export const WIDTH: Partial<Record<Pose, number>> = { cast: 1.9, gatherDown: 1.9 };
